import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { AppPageHeader } from '../../components/app/AppPage';
import {
  Box,
  Button,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  Card,
  CardContent,
  Divider,
  Alert,
  Chip,
  Stack,
  TextField,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { Search as SearchIcon, Info as InfoIcon } from '@mui/icons-material';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`nexus-tabpanel-${index}`}
      aria-labelledby={`nexus-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `nexus-tab-${index}`,
    'aria-controls': `nexus-tabpanel-${index}`,
  };
}

interface NexusThreshold {
  state: string;
  state_name: string;
  sales_threshold: number | null;
  transaction_threshold: number | null;
  effective_date: string;
  measurement_period: string;
  threshold_type: string;
  notes: string;
  digital_goods_taxable: boolean;
  saas_taxable: boolean;
}

// Comprehensive data from deep research
const nexusThresholds: NexusThreshold[] = [
  {
    state: "AL",
    state_name: "Alabama",
    sales_threshold: 250000,
    transaction_threshold: null,
    effective_date: "2019-01-01",
    measurement_period: "ANNUAL",
    threshold_type: "SALES",
    notes: "Previous calendar year.",
    digital_goods_taxable: true,
    saas_taxable: true
  },
  {
    state: "AK",
    state_name: "Alaska",
    sales_threshold: 100000,
    transaction_threshold: 200,
    effective_date: "2020-01-01",
    measurement_period: "ANNUAL",
    threshold_type: "BOTH",
    notes: "No state sales tax, but local jurisdictions may impose sales tax.",
    digital_goods_taxable: false,
    saas_taxable: false
  },
  {
    state: "AZ",
    state_name: "Arizona",
    sales_threshold: 100000,
    transaction_threshold: null,
    effective_date: "2019-10-01",
    measurement_period: "ANNUAL",
    threshold_type: "SALES",
    notes: "Previous or current calendar year.",
    digital_goods_taxable: true,
    saas_taxable: true
  },
  {
    state: "AR",
    state_name: "Arkansas",
    sales_threshold: 100000,
    transaction_threshold: 200,
    effective_date: "2019-07-01",
    measurement_period: "ANNUAL",
    threshold_type: "BOTH",
    notes: "Previous or current calendar year.",
    digital_goods_taxable: true,
    saas_taxable: true
  },
  {
    state: "CA",
    state_name: "California",
    sales_threshold: 500000,
    transaction_threshold: null,
    effective_date: "2019-04-01",
    measurement_period: "ANNUAL",
    threshold_type: "SALES",
    notes: "Prior year or current year calendar year sales.",
    digital_goods_taxable: true,
    saas_taxable: false
  },
  {
    state: "CO",
    state_name: "Colorado",
    sales_threshold: 100000,
    transaction_threshold: null,
    effective_date: "2019-06-01",
    measurement_period: "ANNUAL",
    threshold_type: "SALES",
    notes: "Previous or current calendar year.",
    digital_goods_taxable: true,
    saas_taxable: true
  },
  {
    state: "CT",
    state_name: "Connecticut",
    sales_threshold: 100000,
    transaction_threshold: 200,
    effective_date: "2018-12-01",
    measurement_period: "ROLLING_YEAR",
    threshold_type: "BOTH",
    notes: "12-month period ending on September 30.",
    digital_goods_taxable: true,
    saas_taxable: true
  },
  {
    state: "DE",
    state_name: "Delaware",
    sales_threshold: null,
    transaction_threshold: null,
    effective_date: "N/A",
    measurement_period: "N/A",
    threshold_type: "N/A",
    notes: "No sales tax in Delaware.",
    digital_goods_taxable: false,
    saas_taxable: false
  },
  {
    state: "DC",
    state_name: "District of Columbia",
    sales_threshold: 100000,
    transaction_threshold: 200,
    effective_date: "2019-01-01",
    measurement_period: "ANNUAL",
    threshold_type: "BOTH",
    notes: "Previous or current calendar year.",
    digital_goods_taxable: true,
    saas_taxable: true
  },
  {
    state: "FL",
    state_name: "Florida",
    sales_threshold: 100000,
    transaction_threshold: null,
    effective_date: "2021-07-01",
    measurement_period: "ANNUAL",
    threshold_type: "SALES",
    notes: "Previous calendar year or previous 12 months.",
    digital_goods_taxable: false,
    saas_taxable: false
  },
  {
    state: "GA",
    state_name: "Georgia",
    sales_threshold: 100000,
    transaction_threshold: 200,
    effective_date: "2019-01-01",
    measurement_period: "ANNUAL",
    threshold_type: "BOTH",
    notes: "Prior or current calendar year.",
    digital_goods_taxable: true,
    saas_taxable: true
  },
  {
    state: "HI",
    state_name: "Hawaii",
    sales_threshold: 100000,
    transaction_threshold: 200,
    effective_date: "2018-07-01",
    measurement_period: "ANNUAL",
    threshold_type: "BOTH",
    notes: "Current or preceding calendar year.",
    digital_goods_taxable: true,
    saas_taxable: true
  },
  {
    state: "ID",
    state_name: "Idaho",
    sales_threshold: 100000,
    transaction_threshold: null,
    effective_date: "2019-06-01",
    measurement_period: "ANNUAL",
    threshold_type: "SALES",
    notes: "Current or preceding calendar year.",
    digital_goods_taxable: true,
    saas_taxable: true
  },
  {
    state: "IL",
    state_name: "Illinois",
    sales_threshold: 100000,
    transaction_threshold: 200,
    effective_date: "2020-01-01",
    measurement_period: "ANNUAL",
    threshold_type: "BOTH",
    notes: "Prior 12-month period.",
    digital_goods_taxable: false,
    saas_taxable: true
  },
  {
    state: "IN",
    state_name: "Indiana",
    sales_threshold: 100000,
    transaction_threshold: 200,
    effective_date: "2018-10-01",
    measurement_period: "ANNUAL",
    threshold_type: "BOTH",
    notes: "Current or preceding calendar year.",
    digital_goods_taxable: true,
    saas_taxable: true
  },
  {
    state: "IA",
    state_name: "Iowa",
    sales_threshold: 100000,
    transaction_threshold: null,
    effective_date: "2019-01-01",
    measurement_period: "ANNUAL",
    threshold_type: "SALES",
    notes: "Previous or current calendar year.",
    digital_goods_taxable: true,
    saas_taxable: true
  },
  {
    state: "KS",
    state_name: "Kansas",
    sales_threshold: 100000,
    transaction_threshold: null,
    effective_date: "2019-10-01",
    measurement_period: "ANNUAL",
    threshold_type: "SALES",
    notes: "Current or preceding calendar year.",
    digital_goods_taxable: true,
    saas_taxable: true
  },
  {
    state: "KY",
    state_name: "Kentucky",
    sales_threshold: 100000,
    transaction_threshold: 200,
    effective_date: "2019-07-01",
    measurement_period: "ANNUAL",
    threshold_type: "BOTH",
    notes: "Previous calendar year.",
    digital_goods_taxable: true,
    saas_taxable: true
  },
  {
    state: "LA",
    state_name: "Louisiana",
    sales_threshold: 100000,
    transaction_threshold: 200,
    effective_date: "2020-07-01",
    measurement_period: "ANNUAL",
    threshold_type: "BOTH",
    notes: "Previous or current calendar year.",
    digital_goods_taxable: true,
    saas_taxable: true
  },
  {
    state: "ME",
    state_name: "Maine",
    sales_threshold: 100000,
    transaction_threshold: 200,
    effective_date: "2019-07-01",
    measurement_period: "ANNUAL",
    threshold_type: "BOTH",
    notes: "Previous or current calendar year.",
    digital_goods_taxable: true,
    saas_taxable: true
  },
  {
    state: "MD",
    state_name: "Maryland",
    sales_threshold: 100000,
    transaction_threshold: 200,
    effective_date: "2018-10-01",
    measurement_period: "ANNUAL",
    threshold_type: "BOTH",
    notes: "Previous or current calendar year.",
    digital_goods_taxable: true,
    saas_taxable: true
  },
  {
    state: "MA",
    state_name: "Massachusetts",
    sales_threshold: 100000,
    transaction_threshold: null,
    effective_date: "2019-10-01",
    measurement_period: "ANNUAL",
    threshold_type: "SALES",
    notes: "Previous calendar year.",
    digital_goods_taxable: true,
    saas_taxable: true
  },
  {
    state: "MI",
    state_name: "Michigan",
    sales_threshold: 100000,
    transaction_threshold: 200,
    effective_date: "2018-10-01",
    measurement_period: "ANNUAL",
    threshold_type: "BOTH",
    notes: "Previous calendar year.",
    digital_goods_taxable: true,
    saas_taxable: true
  },
  {
    state: "MN",
    state_name: "Minnesota",
    sales_threshold: 100000,
    transaction_threshold: 200,
    effective_date: "2019-10-01",
    measurement_period: "ANNUAL",
    threshold_type: "BOTH",
    notes: "Previous 12-month period.",
    digital_goods_taxable: true,
    saas_taxable: true
  },
  {
    state: "MS",
    state_name: "Mississippi",
    sales_threshold: 250000,
    transaction_threshold: null,
    effective_date: "2018-09-01",
    measurement_period: "ANNUAL",
    threshold_type: "SALES",
    notes: "Prior 12-month period.",
    digital_goods_taxable: true,
    saas_taxable: true
  },
  {
    state: "MO",
    state_name: "Missouri",
    sales_threshold: 100000,
    transaction_threshold: null,
    effective_date: "2023-01-01",
    measurement_period: "ANNUAL",
    threshold_type: "SALES",
    notes: "Previous 12-month period.",
    digital_goods_taxable: true,
    saas_taxable: true
  },
  {
    state: "MT",
    state_name: "Montana",
    sales_threshold: null,
    transaction_threshold: null,
    effective_date: "N/A",
    measurement_period: "N/A",
    threshold_type: "N/A",
    notes: "No sales tax in Montana.",
    digital_goods_taxable: false,
    saas_taxable: false
  },
  {
    state: "NE",
    state_name: "Nebraska",
    sales_threshold: 100000,
    transaction_threshold: 200,
    effective_date: "2019-04-01",
    measurement_period: "ANNUAL",
    threshold_type: "BOTH",
    notes: "Previous or current calendar year.",
    digital_goods_taxable: true,
    saas_taxable: true
  },
  {
    state: "NV",
    state_name: "Nevada",
    sales_threshold: 100000,
    transaction_threshold: 200,
    effective_date: "2018-10-01",
    measurement_period: "ANNUAL",
    threshold_type: "BOTH",
    notes: "Previous or current calendar year.",
    digital_goods_taxable: true,
    saas_taxable: true
  },
  {
    state: "NH",
    state_name: "New Hampshire",
    sales_threshold: null,
    transaction_threshold: null,
    effective_date: "N/A",
    measurement_period: "N/A",
    threshold_type: "N/A",
    notes: "No sales tax in New Hampshire.",
    digital_goods_taxable: false,
    saas_taxable: false
  },
  {
    state: "NJ",
    state_name: "New Jersey",
    sales_threshold: 100000,
    transaction_threshold: 200,
    effective_date: "2018-11-01",
    measurement_period: "ANNUAL",
    threshold_type: "BOTH",
    notes: "Previous or current calendar year.",
    digital_goods_taxable: true,
    saas_taxable: true
  },
  {
    state: "NM",
    state_name: "New Mexico",
    sales_threshold: 100000,
    transaction_threshold: null,
    effective_date: "2019-07-01",
    measurement_period: "ANNUAL",
    threshold_type: "SALES",
    notes: "Previous calendar year.",
    digital_goods_taxable: true,
    saas_taxable: true
  },
  {
    state: "NY",
    state_name: "New York",
    sales_threshold: 500000,
    transaction_threshold: 100,
    effective_date: "2019-06-01",
    measurement_period: "ROLLING_YEAR",
    threshold_type: "BOTH",
    notes: "Previous four sales tax quarters.",
    digital_goods_taxable: true,
    saas_taxable: true
  },
  {
    state: "NC",
    state_name: "North Carolina",
    sales_threshold: 100000,
    transaction_threshold: 200,
    effective_date: "2018-11-01",
    measurement_period: "ANNUAL",
    threshold_type: "BOTH",
    notes: "Previous or current calendar year.",
    digital_goods_taxable: false,
    saas_taxable: true
  },
  {
    state: "ND",
    state_name: "North Dakota",
    sales_threshold: 100000,
    transaction_threshold: null,
    effective_date: "2018-10-01",
    measurement_period: "ANNUAL",
    threshold_type: "SALES",
    notes: "Previous or current calendar year.",
    digital_goods_taxable: true,
    saas_taxable: true
  },
  {
    state: "OH",
    state_name: "Ohio",
    sales_threshold: 100000,
    transaction_threshold: 200,
    effective_date: "2018-08-01",
    measurement_period: "ANNUAL",
    threshold_type: "BOTH",
    notes: "Current or preceding calendar year.",
    digital_goods_taxable: true,
    saas_taxable: true
  },
  {
    state: "OK",
    state_name: "Oklahoma",
    sales_threshold: 100000,
    transaction_threshold: null,
    effective_date: "2019-11-01",
    measurement_period: "ANNUAL",
    threshold_type: "SALES",
    notes: "Previous or current calendar year.",
    digital_goods_taxable: true,
    saas_taxable: true
  },
  {
    state: "OR",
    state_name: "Oregon",
    sales_threshold: null,
    transaction_threshold: null,
    effective_date: "N/A",
    measurement_period: "N/A",
    threshold_type: "N/A",
    notes: "No sales tax in Oregon.",
    digital_goods_taxable: false,
    saas_taxable: false
  },
  {
    state: "PA",
    state_name: "Pennsylvania",
    sales_threshold: 100000,
    transaction_threshold: null,
    effective_date: "2019-07-01",
    measurement_period: "ANNUAL",
    threshold_type: "SALES",
    notes: "Previous 12-month period.",
    digital_goods_taxable: true,
    saas_taxable: true
  },
  {
    state: "RI",
    state_name: "Rhode Island",
    sales_threshold: 100000,
    transaction_threshold: 200,
    effective_date: "2018-08-17",
    measurement_period: "ANNUAL",
    threshold_type: "BOTH",
    notes: "Previous or current calendar year.",
    digital_goods_taxable: true,
    saas_taxable: true
  },
  {
    state: "SC",
    state_name: "South Carolina",
    sales_threshold: 100000,
    transaction_threshold: null,
    effective_date: "2018-11-01",
    measurement_period: "ANNUAL",
    threshold_type: "SALES",
    notes: "Previous or current calendar year.",
    digital_goods_taxable: true,
    saas_taxable: true
  },
  {
    state: "SD",
    state_name: "South Dakota",
    sales_threshold: 100000,
    transaction_threshold: 200,
    effective_date: "2018-11-01",
    measurement_period: "ANNUAL",
    threshold_type: "BOTH",
    notes: "Previous or current calendar year.",
    digital_goods_taxable: true,
    saas_taxable: true
  },
  {
    state: "TN",
    state_name: "Tennessee",
    sales_threshold: 100000,
    transaction_threshold: null,
    effective_date: "2019-10-01",
    measurement_period: "ANNUAL",
    threshold_type: "SALES",
    notes: "Previous 12-month period.",
    digital_goods_taxable: true,
    saas_taxable: true
  },
  {
    state: "TX",
    state_name: "Texas",
    sales_threshold: 500000,
    transaction_threshold: null,
    effective_date: "2019-10-01",
    measurement_period: "ANNUAL",
    threshold_type: "SALES",
    notes: "Prior calendar year.",
    digital_goods_taxable: true,
    saas_taxable: true
  },
  {
    state: "UT",
    state_name: "Utah",
    sales_threshold: 100000,
    transaction_threshold: 200,
    effective_date: "2019-01-01",
    measurement_period: "ANNUAL",
    threshold_type: "BOTH",
    notes: "Previous or current calendar year.",
    digital_goods_taxable: true,
    saas_taxable: true
  },
  {
    state: "VT",
    state_name: "Vermont",
    sales_threshold: 100000,
    transaction_threshold: 200,
    effective_date: "2018-07-01",
    measurement_period: "ANNUAL",
    threshold_type: "BOTH",
    notes: "Previous 12-month period.",
    digital_goods_taxable: true,
    saas_taxable: true
  },
  {
    state: "VA",
    state_name: "Virginia",
    sales_threshold: 100000,
    transaction_threshold: 200,
    effective_date: "2019-07-01",
    measurement_period: "ANNUAL",
    threshold_type: "BOTH",
    notes: "Previous or current calendar year.",
    digital_goods_taxable: true,
    saas_taxable: true
  },
  {
    state: "WA",
    state_name: "Washington",
    sales_threshold: 100000,
    transaction_threshold: null,
    effective_date: "2018-10-01",
    measurement_period: "ANNUAL",
    threshold_type: "SALES",
    notes: "Prior or current calendar year.",
    digital_goods_taxable: true,
    saas_taxable: true
  },
  {
    state: "WV",
    state_name: "West Virginia",
    sales_threshold: 100000,
    transaction_threshold: 200,
    effective_date: "2019-01-01",
    measurement_period: "ANNUAL",
    threshold_type: "BOTH",
    notes: "Previous calendar year.",
    digital_goods_taxable: true,
    saas_taxable: true
  },
  {
    state: "WI",
    state_name: "Wisconsin",
    sales_threshold: 100000,
    transaction_threshold: null,
    effective_date: "2018-10-01",
    measurement_period: "ANNUAL",
    threshold_type: "SALES",
    notes: "Previous 12-month period.",
    digital_goods_taxable: true,
    saas_taxable: true
  },
  {
    state: "WY",
    state_name: "Wyoming",
    sales_threshold: 100000,
    transaction_threshold: 200,
    effective_date: "2019-07-01",
    measurement_period: "ANNUAL",
    threshold_type: "BOTH",
    notes: "Previous or current calendar year.",
    digital_goods_taxable: true,
    saas_taxable: true
  }
];

const nexusTypes = [
  {
    type: "ECONOMIC",
    description: "Based on sales volume or transaction count in a jurisdiction, without physical presence",
    determination: "Sales volume and/or transaction count exceeding state-defined thresholds",
    examples: "Selling $500,000+ to California customers or 200+ transactions in Illinois",
    wayfair_impact: "Result of the 2018 Supreme Court Wayfair decision, eliminating the physical presence requirement"
  },
  {
    type: "PHYSICAL",
    description: "Traditional nexus based on physical presence in a jurisdiction",
    determination: "Having property, employees, or inventory in a state",
    examples: "Office in New York, employee in Texas, inventory in a California warehouse",
    wayfair_impact: "Pre-dates Wayfair decision, still applies in all states"
  },
  {
    type: "AFFILIATE",
    description: "Relationship-based nexus through related entities",
    determination: "Connection to in-state entities that promote or support sales",
    examples: "In-state affiliate that refers customers, shared branding with in-state retailer",
    wayfair_impact: "Many states enacted prior to Wayfair, still applies in most jurisdictions"
  },
  {
    type: "MARKETPLACE",
    description: "Nexus for platforms facilitating sales by third-party sellers",
    determination: "Marketplace facilitator laws requiring platforms to collect tax",
    examples: "Amazon, eBay, Etsy are required to collect tax on behalf of sellers",
    wayfair_impact: "Most states enacted after Wayfair to capture marketplace sales"
  }
];

const digitalProductRules = [
  {
    category: "Digital Downloads",
    description: "Electronically transferred files like e-books, music, videos, and software",
    state_count: 30,
    typical_exemptions: "Educational materials in some states",
    notes: "Generally taxable in most states but exempt in notable exceptions like NC and FL"
  },
  {
    category: "SaaS (Software as a Service)",
    description: "Cloud-based software accessed via subscription",
    state_count: 23,
    typical_exemptions: "Business-to-business SaaS in some states",
    notes: "Treatment varies significantly by state, with frequent regulatory changes"
  },
  {
    category: "Digital Services",
    description: "Data processing, website design, digital marketing",
    state_count: 18,
    typical_exemptions: "Business services in some states",
    notes: "Most complex category with widely varying interpretations across states"
  },
  {
    category: "Streaming Services",
    description: "Video, music, and content streaming subscriptions",
    state_count: 33,
    typical_exemptions: "Educational content in limited cases",
    notes: "Increasingly taxed as states update laws to capture streaming economy"
  }
];

const NexusRules: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const formatCurrency = (amount: number | null) => {
    if (amount === null) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    if (dateString === 'N/A') return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const filteredThresholds = nexusThresholds.filter(threshold => 
    threshold.state.toLowerCase().includes(searchTerm.toLowerCase()) || 
    threshold.state_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Box className="gt-page">
      <AppPageHeader
        title="Economic nexus rules"
        description="The thresholds that decide when a business must collect and remit sales tax in each state, with the research behind them."
        actions={<Button variant="outlined" component={RouterLink} to="/nexus/dashboard">See your nexus status</Button>}
      />

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="nexus rules tabs">
          <Tab label="State Thresholds" {...a11yProps(0)} />
          <Tab label="Nexus Types" {...a11yProps(1)} />
          <Tab label="Digital Products" {...a11yProps(2)} />
          <Tab label="Determination Process" {...a11yProps(3)} />
        </Tabs>
      </Box>

      <TabPanel value={tabValue} index={0}>
        <Alert severity="info" sx={{ mb: 3 }}>
          Economic nexus thresholds determine when remote sellers must register and collect sales tax. These thresholds vary by state and are subject to change.
        </Alert>

        <TextField
          label="Search States"
          variant="outlined"
          fullWidth
          sx={{ mb: 3 }}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />

        <TableContainer component={Paper} elevation={2}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>State</TableCell>
                <TableCell>Sales Threshold</TableCell>
                <TableCell>Transaction Threshold</TableCell>
                <TableCell>Threshold Type</TableCell>
                <TableCell>Measurement Period</TableCell>
                <TableCell>Effective Date</TableCell>
                <TableCell>Digital Goods</TableCell>
                <TableCell>SaaS</TableCell>
                <TableCell>Notes</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredThresholds.map((row) => (
                <TableRow key={row.state}>
                  <TableCell>
                    <strong>{row.state_name}</strong> ({row.state})
                  </TableCell>
                  <TableCell>{formatCurrency(row.sales_threshold)}</TableCell>
                  <TableCell>
                    {row.transaction_threshold ? `${row.transaction_threshold} transactions` : 'N/A'}
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={row.threshold_type === 'BOTH' ? 'Sales OR Transactions' : 'Sales Only'} 
                      color={row.threshold_type === 'BOTH' ? 'primary' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{row.measurement_period.replace('_', ' ')}</TableCell>
                  <TableCell>{formatDate(row.effective_date)}</TableCell>
                  <TableCell>
                    <Chip 
                      label={row.digital_goods_taxable ? 'Taxable' : 'Not Taxable'} 
                      color={row.digital_goods_taxable ? 'success' : 'error'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={row.saas_taxable ? 'Taxable' : 'Not Taxable'} 
                      color={row.saas_taxable ? 'success' : 'error'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{row.notes}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
          {nexusTypes.map((type) => (
            <Card key={type.type} elevation={3}>
              <CardContent>
                <Typography variant="h6" color="primary" gutterBottom>
                  {type.type.charAt(0) + type.type.slice(1).toLowerCase()} Nexus
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="body2" paragraph>
                  <strong>Definition:</strong> {type.description}
                </Typography>
                <Typography variant="body2" paragraph>
                  <strong>Determination:</strong> {type.determination}
                </Typography>
                <Typography variant="body2" paragraph>
                  <strong>Examples:</strong> {type.examples}
                </Typography>
                <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                  <strong>Wayfair Impact:</strong> {type.wayfair_impact}
                </Typography>
              </CardContent>
            </Card>
          ))}
        </Box>
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        <Typography variant="h6" gutterBottom>
          Digital Products Taxability by Category
        </Typography>

        <Alert severity="warning" sx={{ mb: 3 }}>
          Digital products taxability is one of the most complex and rapidly changing areas of sales tax law. Rules vary significantly by state and product type.
        </Alert>

        <TableContainer component={Paper} elevation={2} sx={{ mb: 4 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Category</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Taxed in # States</TableCell>
                <TableCell>Typical Exemptions</TableCell>
                <TableCell>Notes</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {digitalProductRules.map((row) => (
                <TableRow key={row.category}>
                  <TableCell><strong>{row.category}</strong></TableCell>
                  <TableCell>{row.description}</TableCell>
                  <TableCell>{row.state_count} states</TableCell>
                  <TableCell>{row.typical_exemptions}</TableCell>
                  <TableCell>{row.notes}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Typography variant="subtitle1" gutterBottom>
          Key States with Unique Digital Product Taxation Rules:
        </Typography>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 2 }}>
          <Card>
            <CardContent>
              <Typography variant="subtitle1" color="primary.main">California</Typography>
              <Divider sx={{ my: 1 }} />
              <Typography variant="body2">
                Taxes digital downloads but not cloud-based services (SaaS). Enacted significant changes to digital marketplace laws in 2023.
              </Typography>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent>
              <Typography variant="subtitle1" color="primary.main">New York</Typography>
              <Divider sx={{ my: 1 }} />
              <Typography variant="body2">
                Broadly taxes digital products including SaaS, streaming, and data processing. Has complex sourcing rules for digital services.
              </Typography>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent>
              <Typography variant="subtitle1" color="primary.main">Florida</Typography>
              <Divider sx={{ my: 1 }} />
              <Typography variant="body2">
                Historically did not tax digital products, but enacted marketplace facilitator laws in 2021. Still maintains many digital exemptions.
              </Typography>
            </CardContent>
          </Card>
        </Box>
      </TabPanel>

      <TabPanel value={tabValue} index={3}>
        <Typography variant="h6" gutterBottom>
          Nexus Determination Process
        </Typography>
        
        <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
          <Typography variant="subtitle1" gutterBottom>
            The system uses the following process to determine nexus status:
          </Typography>
          
          <Box component="ol" sx={{ ml: 2 }}>
            <Box component="li" sx={{ mb: 1 }}>
              <Typography>
                <strong>Data Collection:</strong> Retrieve all transactions for the company within the lookback period (typically 12 months)
              </Typography>
            </Box>
            <Box component="li" sx={{ mb: 1 }}>
              <Typography>
                <strong>Aggregation:</strong> Calculate total sales and transaction counts by state based on shipping addresses
              </Typography>
            </Box>
            <Box component="li" sx={{ mb: 1 }}>
              <Typography>
                <strong>Threshold Comparison:</strong> Compare state totals against thresholds from the <code>TaxThreshold</code> table
              </Typography>
            </Box>
            <Box component="li" sx={{ mb: 1 }}>
              <Typography>
                <strong>Physical Presence Check:</strong> Verify if company has physical locations in any states from <code>NexusLocation</code> table
              </Typography>
            </Box>
            <Box component="li" sx={{ mb: 1 }}>
              <Typography>
                <strong>Status Determination:</strong> Mark states as:
                <ul>
                  <li>Exceeded: When threshold is met or physical presence exists</li>
                  <li>Approaching: When within 75-99% of threshold</li>
                  <li>Safe: When below 75% of threshold with no physical presence</li>
                </ul>
              </Typography>
            </Box>
            <Box component="li">
              <Typography>
                <strong>Database Update:</strong> Save current status to <code>TaxNexusStatus</code> table for dashboard display
              </Typography>
            </Box>
          </Box>
        </Paper>
        
        <Typography variant="h6" gutterBottom>
          Database Tables for Nexus Tracking
        </Typography>
        
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 3 }}>
          <Card elevation={2}>
            <CardContent>
              <Typography variant="subtitle1" color="primary.main">TaxThreshold</Typography>
              <Divider sx={{ my: 1 }} />
              <Typography variant="body2" sx={{ mb: 1 }}>
                Defines sales and transaction thresholds for each jurisdiction
              </Typography>
              <Box component="code" sx={{ display: 'block', bgcolor: 'grey.100', p: 1, borderRadius: 1, fontSize: '0.8rem' }}>
                jurisdiction_id, sales_threshold, transaction_threshold, effective_date, measurement_period
              </Box>
            </CardContent>
          </Card>
          
          <Card elevation={2}>
            <CardContent>
              <Typography variant="subtitle1" color="primary.main">TaxNexusStatus</Typography>
              <Divider sx={{ my: 1 }} />
              <Typography variant="body2" sx={{ mb: 1 }}>
                Tracks current nexus status for each company-jurisdiction pair
              </Typography>
              <Box component="code" sx={{ display: 'block', bgcolor: 'grey.100', p: 1, borderRadius: 1, fontSize: '0.8rem' }}>
                company_id, jurisdiction_id, has_nexus, nexus_type, current_sales, current_transactions
              </Box>
            </CardContent>
          </Card>
          
          <Card elevation={2}>
            <CardContent>
              <Typography variant="subtitle1" color="primary.main">NexusRule</Typography>
              <Divider sx={{ my: 1 }} />
              <Typography variant="body2" sx={{ mb: 1 }}>
                Contains jurisdiction-specific rules for nexus determination
              </Typography>
              <Box component="code" sx={{ display: 'block', bgcolor: 'grey.100', p: 1, borderRadius: 1, fontSize: '0.8rem' }}>
                jurisdiction_id, rule_type, rule_value, effective_date, is_active
              </Box>
            </CardContent>
          </Card>
        </Box>
      </TabPanel>
    </Box>
  );
};

export default NexusRules; 